import { useContext } from 'react';

import { RbacPermissionsContext } from 'components/RbacPermissionsProvider';

// to add a new permission:
// 1. add an enum constant here
// 2. add the developer-friendly key to the allPermissions type
// 3. add logic for the key in generateRbacPermissions

export enum ERawPermission {
  EXPERIMENT_CREATE = 'experiment.create',
  EXPERIMENT_UPDATE = 'experiment.update',
  EXPERIMENT_DELETE = 'experiment.delete',

  DEFINITION_CREATE = 'definitions.create',
  DEFINITION_UPDATE = 'definitions.update',
  DEFINITION_DELETE = 'definitions.delete',

  METRIC_CREATE = 'metric.create',
  METRIC_UPDATE = 'metric.update',
  METRIC_DELETE = 'metric.delete',

  FEATUREFLAG_CREATE = 'feature_flag.create',
  FEATUREFLAG_UPDATE = 'feature_flag.update',

  ADMIN_READ = 'admin.read',

  COMPANYUSERS_READ = 'company_users.read',
  COMPANYUSERS_CREATE = 'company_users.create',
  COMPANYUSERS_UPDATE = 'company_users.update',
  COMPANYUSERS_DELETE = 'company_users.delete',

  SLACK_CREATE = 'slack_notifications.create',
  SLACK_UPDATE = 'slack_notifications.update',
  SLACK_DELETE = 'slack_notifications.delete',

  APIKEYS_READ = 'api_keys.read',

  ENVIRONMENTS_CREATE = 'environment.create',
  ENVIRONMENTS_UPDATE = 'environment.update',
  ENVIRONMENTS_DELETE = 'environment.delete',

  SDKKEYS_CREATE = 'sdk_key.create',
  SDKKEYS_UPDATE = 'sdk_key.update',
  SDKKEYS_DELETE = 'sdk_key.delete',

  BROWSER_EXTENSION_KEYS_CREATE = 'browser_extension_key.create',
  BROWSER_EXTENSION_KEYS_UPDATE = 'browser_extension_key.update',
  BROWSER_EXTENSION_KEYS_DELETE = 'browser_extension_key.delete',

  HOLDOUT_CREATE = 'holdout.create',
  HOLDOUT_UPDATE = 'holdout.update',
  HOLDOUT_DELETE = 'holdout.delete',

  QUASI_EXPERIMENT_CREATE = 'quasi_experiment.create',
  QUASI_EXPERIMENT_UPDATE = 'quasi_experiment.update',
  QUASI_EXPERIMENT_DELETE = 'quasi_experiment.delete',

  TEAM_CREATE = 'team.create',
  TEAM_UPDATE = 'team.update',
  TEAM_DELETE = 'team.delete',

  FEATURE_FLAG_SUGGESTION_CREATE = 'feature_flag_suggestion.create',
  FEATURE_FLAG_SUGGESTION_UPDATE = 'feature_flag_suggestion.update',
  FEATURE_FLAG_SUGGESTION_DELETE = 'feature_flag_suggestion.delete',
  FEATURE_FLAG_SUGGESTION_REVIEW = 'feature_flag_suggestion.review',

  ENTRYPOINT_CREATE = 'entry_points.create',
  ENTRYPOINT_UPDATE = 'entry_points.update',
  ENTRYPOINT_DELETE = 'entry_points.delete',

  DATABASE_CONNECTION_CREATE = 'database_connection_config.create',
  DATABASE_CONNECTION_UPDATE = 'database_connection_config.update',
  DATABASE_CONNECTION_DELETE = 'database_connection_config.delete',

  METRIC_COLLECTION_CREATE = 'metric_collection.create',
  METRIC_COLLECTION_UPDATE = 'metric_collection.update',
  METRIC_COLLECTION_DELETE = 'metric_collection.delete',

  SEGMENT_CREATE = 'segment.create',
  SEGMENT_UPDATE = 'segment.update',
  SEGMENT_DELETE = 'segment.delete',

  SAMPLE_SIZE_CALCULATION_CREATE = 'sample_size_calculation.create',
  SAMPLE_SIZE_CALCULATION_UPDATE = 'sample_size_calculation.update',
  SAMPLE_SIZE_CALCULATION_DELETE = 'sample_size_calculation.delete',

  SWITCHBACK_EXPERIMENT_CREATE = 'switchback_experiment.create',
  SWITCHBACK_EXPERIMENT_UPDATE = 'switchback_experiment.update',
  SWITCHBACK_EXPERIMENT_DELETE = 'switchback_experiment.delete',

  METRIC_SYNC_READ = 'metric_sync.read',
  METRIC_SYNC_CREATE = 'metric_sync.create',
  METRIC_SYNC_UPDATE = 'metric_sync.update',
  METRIC_SYNC_DELETE = 'metric_sync.delete',

  WEBHOOK_READ = 'webhooks.read',
  ANALYSIS_PLAN_READ = 'analysis_plan.read',
  DATABASE_CONNECTION_READ = 'database_connection_config.read',
  SCHEDULES_READ = 'schedules.read',
  METRIC_PROPERTY_READ = 'metric_property.read',
  METRIC_VOLUME_READ = 'metric_volume.read',
  WORKFLOW_READ = 'workflow.read',

  TARGETING_ATTRIBUTES_READ = 'targeting_attributes.read',
  TARGETING_ATTRIBUTES_CREATE = 'targeting_attributes.create',
  TARGETING_ATTRIBUTES_UPDATE = 'targeting_attributes.update',
  TARGETING_ATTRIBUTES_DELETE = 'targeting_attributes.delete',

  PROTOCOL_READ = 'protocol.read',
  PROTOCOL_CREATE = 'protocol.create',
  PROTOCOL_UPDATE = 'protocol.update',
  PROTOCOL_DELETE = 'protocol.delete',

  DECISION_CRITERIA_UPDATE = 'decision_criteria.update',

  PLANNING_CALCULATOR_CREATE = 'planning_calculator.create',
  PLANNING_CALCULATOR_UPDATE = 'planning_calculator.update',
  PLANNING_CALCULATOR_DELETE = 'planning_calculator.delete',

  TAG_CREATE = 'tags.create',
  TAG_UPDATE = 'tags.update',
  TAG_DELETE = 'tags.delete',

  METADATA_FIELD_CREATE = 'metadata.create',
  METADATA_FIELD_UPDATE = 'metadata.update',
  METADATA_FIELD_DELETE = 'metadata.delete',

  CERTIFICATION_CREATE = 'certification.create',
  CERTIFICATION_UPDATE = 'certification.update',
  CERTIFICATION_DELETE = 'certification.delete',

  FLAG_CONFIG_DEBUG_READ = 'flag_config_debug.read',
  FLAG_CONFIG_DEBUG_UPDATE = 'flag_config_debug.update',
}

export default function useRbacPermissions() {
  const { rbacPermissions } = useContext(RbacPermissionsContext);
  return rbacPermissions;
}

type allPermissions =
  | 'canCreateExperiment'
  | 'canUpdateExperiment'
  | 'canDeleteExperiment'
  | 'canCreateDefinition'
  | 'canUpdateDefinition'
  | 'canDeleteDefinition'
  | 'canCreateMetric'
  | 'canUpdateMetric'
  | 'canDeleteMetric'
  | 'canCreateFeatureFlag'
  | 'canUpdateFeatureFlag'
  | 'canCreateSlack'
  | 'canUpdateSlack'
  | 'canDeleteSlack'
  | 'canReadAdmin'
  | 'canReadApiKeys'
  | 'canReadCompanyUsers'
  | 'canCreateCompanyUsers'
  | 'canUpdateCompanyUsers'
  | 'canDeleteCompanyUsers'
  | 'canCreateEnvironment'
  | 'canUpdateEnvironment'
  | 'canDeleteEnvironment'
  | 'canCreateSdkKey'
  | 'canUpdateSdkKey'
  | 'canDeleteSdkKey'
  | 'canCreateBrowserExtensionKey'
  | 'canUpdateBrowserExtensionKey'
  | 'canDeleteBrowserExtensionKey'
  | 'canCreateHoldout'
  | 'canUpdateHoldout'
  | 'canDeleteHoldout'
  | 'canCreateQuasiExperiment'
  | 'canUpdateQuasiExperiment'
  | 'canDeleteQuasiExperiment'
  | 'canCreateSwitchbackExperiment'
  | 'canUpdateSwitchbackExperiment'
  | 'canDeleteSwitchbackExperiment'
  | 'canCreateTeam'
  | 'canUpdateTeam'
  | 'canDeleteTeam'
  | 'canCreateFeatureFlagSuggestion'
  | 'canUpdateFeatureFlagSuggestion'
  | 'canDeleteFeatureFlagSuggestion'
  | 'canReviewFeatureFlagSuggestion'
  | 'canCreateEntrypoint'
  | 'canUpdateEntrypoint'
  | 'canDeleteEntrypoint'
  | 'canCreateDatabaseConnection'
  | 'canUpdateDatabaseConnection'
  | 'canDeleteDatabaseConnection'
  | 'canCreateMetricCollection'
  | 'canUpdateMetricCollection'
  | 'canDeleteMetricCollection'
  | 'canCreateSegment'
  | 'canUpdateSegment'
  | 'canDeleteSegment'
  | 'canCreateSampleSizeCalculation'
  | 'canUpdateSampleSizeCalculation'
  | 'canDeleteSampleSizeCalculation'
  | 'canReadMetricSync'
  | 'canCreateMetricSync'
  | 'canUpdateMetricSync'
  | 'canDeleteMetricSync'
  | 'canReadWebhooks'
  | 'canReadAnalysisPlan'
  | 'canReadDatabaseConnection'
  | 'canReadSchedules'
  | 'canReadWorkflow'
  | 'canReadTargetingAttributes'
  | 'canCreateTargetingAttributes'
  | 'canUpdateTargetingAttributes'
  | 'canDeleteTargetingAttributes'
  | 'canReadProtocols'
  | 'canReadMetricProperty'
  | 'canReadMetricVolume'
  | 'canUpdateCompanyDecisionCriteria'
  | 'canCreatePlanningCalculation'
  | 'canUpdatePlanningCalculation'
  | 'canDeletePlanningCalculation'
  | 'canCreateTag'
  | 'canUpdateTag'
  | 'canDeleteTag'
  | 'canCreateMetadataField'
  | 'canUpdateMetadataField'
  | 'canDeleteMetadataField'
  | 'canCreateCertification'
  | 'canUpdateCertification'
  | 'canDeleteCertification'
  | 'canReadFlagConfigDebug'
  | 'canUpdateFlagConfigDebug';

export type RbacPermissionsRecord = Record<allPermissions, boolean>;

export function generateRbacPermissions(permissions: readonly string[]): RbacPermissionsRecord {
  return {
    canCreateExperiment: canDo(permissions, ERawPermission.EXPERIMENT_CREATE),
    canUpdateExperiment: canDo(permissions, ERawPermission.EXPERIMENT_UPDATE),
    canDeleteExperiment: canDo(permissions, ERawPermission.EXPERIMENT_DELETE),

    canCreateDefinition: canDo(permissions, ERawPermission.DEFINITION_CREATE),
    canUpdateDefinition: canDo(permissions, ERawPermission.DEFINITION_UPDATE),
    canDeleteDefinition: canDo(permissions, ERawPermission.DEFINITION_DELETE),

    canCreateMetric: canDo(permissions, ERawPermission.METRIC_CREATE),
    canUpdateMetric: canDo(permissions, ERawPermission.METRIC_UPDATE),
    canDeleteMetric: canDo(permissions, ERawPermission.METRIC_DELETE),

    canCreateFeatureFlag: canDo(permissions, ERawPermission.FEATUREFLAG_CREATE),
    canUpdateFeatureFlag: canDo(permissions, ERawPermission.FEATUREFLAG_UPDATE),

    canCreateSlack: canDo(permissions, ERawPermission.SLACK_CREATE),
    canUpdateSlack: canDo(permissions, ERawPermission.SLACK_UPDATE),
    canDeleteSlack: canDo(permissions, ERawPermission.SLACK_DELETE),

    canReadAdmin: canDo(permissions, ERawPermission.ADMIN_READ),

    canReadApiKeys: canDo(permissions, ERawPermission.APIKEYS_READ),

    canCreateEnvironment: canDo(permissions, ERawPermission.ENVIRONMENTS_CREATE),
    canUpdateEnvironment: canDo(permissions, ERawPermission.ENVIRONMENTS_UPDATE),
    canDeleteEnvironment: canDo(permissions, ERawPermission.ENVIRONMENTS_DELETE),

    canCreateSdkKey: canDo(permissions, ERawPermission.SDKKEYS_CREATE),
    canUpdateSdkKey: canDo(permissions, ERawPermission.SDKKEYS_UPDATE),
    canDeleteSdkKey: canDo(permissions, ERawPermission.SDKKEYS_DELETE),

    canCreateBrowserExtensionKey: canDo(permissions, ERawPermission.BROWSER_EXTENSION_KEYS_CREATE),
    canUpdateBrowserExtensionKey: canDo(permissions, ERawPermission.BROWSER_EXTENSION_KEYS_UPDATE),
    canDeleteBrowserExtensionKey: canDo(permissions, ERawPermission.BROWSER_EXTENSION_KEYS_DELETE),

    canReadCompanyUsers: canDo(permissions, ERawPermission.COMPANYUSERS_READ),
    canCreateCompanyUsers: canDo(permissions, ERawPermission.COMPANYUSERS_CREATE),
    canUpdateCompanyUsers: canDo(permissions, ERawPermission.COMPANYUSERS_UPDATE),
    canDeleteCompanyUsers: canDo(permissions, ERawPermission.COMPANYUSERS_DELETE),

    canCreateHoldout: canDo(permissions, ERawPermission.HOLDOUT_CREATE),
    canUpdateHoldout: canDo(permissions, ERawPermission.HOLDOUT_UPDATE),
    canDeleteHoldout: canDo(permissions, ERawPermission.HOLDOUT_DELETE),

    canCreateQuasiExperiment: canDo(permissions, ERawPermission.QUASI_EXPERIMENT_CREATE),
    canUpdateQuasiExperiment: canDo(permissions, ERawPermission.QUASI_EXPERIMENT_UPDATE),
    canDeleteQuasiExperiment: canDo(permissions, ERawPermission.QUASI_EXPERIMENT_DELETE),

    canCreateSwitchbackExperiment: canDo(permissions, ERawPermission.SWITCHBACK_EXPERIMENT_CREATE),
    canUpdateSwitchbackExperiment: canDo(permissions, ERawPermission.SWITCHBACK_EXPERIMENT_UPDATE),
    canDeleteSwitchbackExperiment: canDo(permissions, ERawPermission.SWITCHBACK_EXPERIMENT_DELETE),

    canCreateTeam: canDo(permissions, ERawPermission.TEAM_CREATE),
    canUpdateTeam: canDo(permissions, ERawPermission.TEAM_UPDATE),
    canDeleteTeam: canDo(permissions, ERawPermission.TEAM_DELETE),

    canCreateFeatureFlagSuggestion: canDo(
      permissions,
      ERawPermission.FEATURE_FLAG_SUGGESTION_CREATE,
    ),
    canUpdateFeatureFlagSuggestion: canDo(
      permissions,
      ERawPermission.FEATURE_FLAG_SUGGESTION_UPDATE,
    ),
    canDeleteFeatureFlagSuggestion: canDo(
      permissions,
      ERawPermission.FEATURE_FLAG_SUGGESTION_DELETE,
    ),
    canReviewFeatureFlagSuggestion: canDo(
      permissions,
      ERawPermission.FEATURE_FLAG_SUGGESTION_REVIEW,
    ),

    canCreateEntrypoint: canDo(permissions, ERawPermission.ENTRYPOINT_CREATE),
    canUpdateEntrypoint: canDo(permissions, ERawPermission.ENTRYPOINT_UPDATE),
    canDeleteEntrypoint: canDo(permissions, ERawPermission.ENTRYPOINT_DELETE),

    canCreateDatabaseConnection: canDo(permissions, ERawPermission.DATABASE_CONNECTION_CREATE),
    canUpdateDatabaseConnection: canDo(permissions, ERawPermission.DATABASE_CONNECTION_UPDATE),
    canDeleteDatabaseConnection: canDo(permissions, ERawPermission.DATABASE_CONNECTION_DELETE),

    canCreateMetricCollection: canDo(permissions, ERawPermission.METRIC_COLLECTION_CREATE),
    canUpdateMetricCollection: canDo(permissions, ERawPermission.METRIC_COLLECTION_UPDATE),
    canDeleteMetricCollection: canDo(permissions, ERawPermission.METRIC_COLLECTION_DELETE),

    canCreateSegment: canDo(permissions, ERawPermission.SEGMENT_CREATE),
    canUpdateSegment: canDo(permissions, ERawPermission.SEGMENT_UPDATE),
    canDeleteSegment: canDo(permissions, ERawPermission.SEGMENT_DELETE),

    canCreateSampleSizeCalculation: canDo(
      permissions,
      ERawPermission.SAMPLE_SIZE_CALCULATION_CREATE,
    ),
    canUpdateSampleSizeCalculation: canDo(
      permissions,
      ERawPermission.SAMPLE_SIZE_CALCULATION_UPDATE,
    ),
    canDeleteSampleSizeCalculation: canDo(
      permissions,
      ERawPermission.SAMPLE_SIZE_CALCULATION_DELETE,
    ),

    canCreatePlanningCalculation: canDo(permissions, ERawPermission.PLANNING_CALCULATOR_CREATE),
    canUpdatePlanningCalculation: canDo(permissions, ERawPermission.PLANNING_CALCULATOR_UPDATE),
    canDeletePlanningCalculation: canDo(permissions, ERawPermission.PLANNING_CALCULATOR_DELETE),

    canReadMetricSync: canDo(permissions, ERawPermission.METRIC_SYNC_READ),
    canCreateMetricSync: canDo(permissions, ERawPermission.METRIC_SYNC_CREATE),
    canUpdateMetricSync: canDo(permissions, ERawPermission.METRIC_SYNC_UPDATE),
    canDeleteMetricSync: canDo(permissions, ERawPermission.METRIC_SYNC_DELETE),

    canReadWebhooks: canDo(permissions, ERawPermission.WEBHOOK_READ),
    canReadAnalysisPlan: canDo(permissions, ERawPermission.ANALYSIS_PLAN_READ),
    canReadDatabaseConnection: canDo(permissions, ERawPermission.DATABASE_CONNECTION_READ),
    canReadSchedules: canDo(permissions, ERawPermission.SCHEDULES_READ),
    canReadMetricProperty: canDo(permissions, ERawPermission.METRIC_PROPERTY_READ),
    canReadMetricVolume: canDo(permissions, ERawPermission.METRIC_VOLUME_READ),
    canReadWorkflow: canDo(permissions, ERawPermission.WORKFLOW_READ),

    canReadTargetingAttributes: canDo(permissions, ERawPermission.TARGETING_ATTRIBUTES_READ),
    canCreateTargetingAttributes: canDo(permissions, ERawPermission.TARGETING_ATTRIBUTES_CREATE),
    canUpdateTargetingAttributes: canDo(permissions, ERawPermission.TARGETING_ATTRIBUTES_UPDATE),
    canDeleteTargetingAttributes: canDo(permissions, ERawPermission.TARGETING_ATTRIBUTES_DELETE),

    canReadProtocols: canDo(permissions, ERawPermission.PROTOCOL_READ),
    canUpdateCompanyDecisionCriteria: canDo(permissions, ERawPermission.DECISION_CRITERIA_UPDATE),

    canCreateTag: canDo(permissions, ERawPermission.TAG_CREATE),
    canUpdateTag: canDo(permissions, ERawPermission.TAG_UPDATE),
    canDeleteTag: canDo(permissions, ERawPermission.TAG_DELETE),

    canCreateMetadataField: canDo(permissions, ERawPermission.METADATA_FIELD_CREATE),
    canUpdateMetadataField: canDo(permissions, ERawPermission.METADATA_FIELD_UPDATE),
    canDeleteMetadataField: canDo(permissions, ERawPermission.METADATA_FIELD_DELETE),

    canCreateCertification: canDo(permissions, ERawPermission.CERTIFICATION_CREATE),
    canUpdateCertification: canDo(permissions, ERawPermission.CERTIFICATION_UPDATE),
    canDeleteCertification: canDo(permissions, ERawPermission.CERTIFICATION_DELETE),

    canReadFlagConfigDebug: canDo(permissions, ERawPermission.FLAG_CONFIG_DEBUG_READ),
    canUpdateFlagConfigDebug: canDo(permissions, ERawPermission.FLAG_CONFIG_DEBUG_UPDATE),
  };
}

function canDo(permissions: readonly string[], permission: string) {
  return permissions.some((allowed) => allowed === permission);
}
